/**
 * 月次締めの画面（F-11 自動検算の結果 ／ F-23 モバイル締め・M9c）。
 *
 * ★**判断を DOM から切り離す。** 並べ替え・件数・申請できるかどうか・
 *   出す文言をここで決め、`main.ts` は描くだけにする。
 *   ★2026-08-25 / M6c で `tests/ui/main-dom.test.ts`（jsdom）が入ったが、
 *   **この画面の `append` はまだ固定していない**（固定したのは M5b / M5f / PR #49 の3箇所）。
 *
 * ## ★この画面がいちばん伝えなければならないこと
 *
 * 「締められません」ではなく、**「何がまだ検査されていないか」。**
 * 計算定義書 §9.1 は `skipped`（検証できなかった）も申請を止めると定めている。
 * ★止めているのが `failed`（数字が合っていない）なのか `skipped`（そもそも見ていない）なのかは、
 *   利用者にとって**まったく別の話**——前者は直す、後者は待つか入力する。**混ぜない。**
 */

import type { ClosingStatus, IntegrityCheck } from "./api.js";

/** 状態の並び順。★`failed` を先頭に固定する。眺めて探させない */
const ORDER: Record<IntegrityCheck["status"], number> = { failed: 0, skipped: 1, passed: 2 };

export const STATUS_LABEL: Record<IntegrityCheck["status"], string> = {
  passed: "一致",
  failed: "不一致",
  skipped: "未検証",
};

/*
 * ★締め状態の表示名を**ここに持たない。** `main.ts` が全タブ共通のシェルで既に出しており、
 *   2つ持つと片方だけ直したときに、同じ画面に違う言い方が並ぶ
 *   （同じ指標を2箇所で別々に持つ形＝絶対ルール5の親戚。2026-08-20・spec の指摘で消した）。
 */

export interface ClosingSummary {
  /** 表示順に並べ替えた全件。★件数は畳まない */
  ordered: IntegrityCheck[];
  counts: { passed: number; failed: number; skipped: number; total: number };
  /** 申請できるか。★`passed` 以外が1件でもあれば false（§9.1） */
  canSubmit: boolean;
  /** 申請できない理由の1文。★できるときは `null` */
  reason: string | null;
  /** 申請できる状態の月か（`OPEN` / `REOPENED` 以外は押させない） */
  stateAllows: boolean;
}

/**
 * 検算の一覧から、画面が必要とする判断を全部作る。
 *
 * ★**「◯件中◯件」を必ず出す。** 「問題なし」だけだと、
 *   1件も検査していない状態と見分けが付かない（`.loop/memory.md`）。
 */
export function summarizeChecks(
  checks: readonly IntegrityCheck[],
  closingStatus: ClosingStatus,
): ClosingSummary {
  const counts = {
    passed: checks.filter((c) => c.status === "passed").length,
    failed: checks.filter((c) => c.status === "failed").length,
    skipped: checks.filter((c) => c.status === "skipped").length,
    total: checks.length,
  };
  const ordered = [...checks].sort(
    (a, b) => ORDER[a.status] - ORDER[b.status] || a.code.localeCompare(b.code),
  );
  // ★差戻しの月もそのまま申請できる（サーバが OPEN を経由させる。M10）
  const stateAllows = closingStatus === "OPEN" || closingStatus === "REOPENED" || closingStatus === "REJECTED";

  return { ordered, counts, canSubmit: canSubmit(counts, stateAllows), reason: reasonFor(counts, closingStatus), stateAllows };
}

const canSubmit = (
  counts: ClosingSummary["counts"],
  stateAllows: boolean,
): boolean => stateAllows && counts.total > 0 && counts.failed === 0 && counts.skipped === 0;

/**
 * 申請できない理由。★**`failed` と `skipped` を1文に混ぜない。**
 *
 * 「1件でも failed があれば押させない」（権限マトリクス §4）だけを伝えると、
 * 未検証しか無い月に「数字が合っていない」と読まれる。やることが違う。
 */
function reasonFor(
  counts: ClosingSummary["counts"],
  closingStatus: ClosingStatus,
): string | null {
  if (closingStatus === "SUBMITTED") return "すでに申請済みです。オーナーの確定を待ってください。";
  if (closingStatus === "CLOSED") return "この月は締め済みです。";
  if (counts.total === 0) return "自動検算をまだ実行していません。";

  const parts: string[] = [];
  if (counts.failed > 0) parts.push(`${String(counts.failed)}件の数字が合っていません`);
  if (counts.skipped > 0) parts.push(`${String(counts.skipped)}件がまだ検証できていません`);
  if (parts.length === 0) return null;
  return `${parts.join("。")}。すべて解消するまで締め申請はできません。`;
}

/**
 * ★**`passed` でも伝えなければならないこと。**
 *
 * C-03 / C-04 は、**`passed` でも `detail` に中身が入る最初の検査**。
 * それまでの5件（C-07 / C-08 / C-10 / C-11 / C-14）は「食い違いが無ければ `detail` も空」
 * だったので、`describeCheck` が `passed` を固定文で返しても何も落ちなかった。
 *
 * ★**この検算が拾ったものを、`detail` の中で止めない。**
 *   `detail` は API と `integrity_check_results` には残るが、**画面には一度も出ない。**
 *   「判定を返せることと、判定が誰かの目に触れることは別」（`.loop/memory.md`）。
 *   §2.2 は休業日売上を「列挙して見せる」と、検算とは別に**表示の要件**として定めている。
 *
 * ★検査コードで分岐しない。**`detail` の項目名で拾う。**
 *   次に同じ形の検算が増えたとき、ここへ来ずに済ませたいため。
 */
export function notesFor(check: IntegrityCheck): string[] {
  const d = check.detail;
  if (d === null) return [];
  const notes: string[] = [];

  const closed = d["closedDaysWithSales"];
  if (Array.isArray(closed) && closed.length > 0) {
    /* ★1か月ぶんは最大31日。全部並べると読めないので5日で畳む */
    const shown = closed.slice(0, 5).join("、");
    const rest = closed.length - 5;
    notes.push(
      `休業日に売上が記録されています：${shown}${rest > 0 ? ` ほか${String(rest)}日` : ""}`,
    );
  }

  /*
   * ★`skipped` のときは理由の文が既に承認待ちを言っている。同じことを2回言わない
   *   （`judgeMonthlyPurchases`）。
   */
  const count = d["unconfirmedCount"];
  const total = d["unconfirmedTotal"];
  if (check.status !== "skipped" && typeof count === "number" && count > 0) {
    const amount = typeof total === "number" ? `${total.toLocaleString()}円` : "";
    notes.push(
      `承認待ちの仕入が ${String(count)}件 ${amount}あります。この検算には入っていません`,
    );
  }

  /*
   * ★**営業日数は passed のときこそ出す。**
   *   現行Excelは4シートに 23 / 30 / 22 / 30 と手入力された営業日数が併存し、
   *   正しい 26日との食い違いが13年見えなかった（D-06）。
   *   「一致しました」だけを出すと、**何と何が 26 で一致したのかが画面に残らない。**
   */
  const openDays = d["openDays"];
  const calendarDays = d["calendarDays"];
  if (check.status !== "skipped" && typeof openDays === "number" && typeof calendarDays === "number") {
    notes.push(
      `この月の営業日数は ${String(openDays)}日です（暦日 ${String(calendarDays)}日）`,
    );
  }

  /* ★どの科目がいくらずれているか。合計だけ出しても直す順番を決められない */
  const mismatched = d["mismatchedAccounts"];
  if (Array.isArray(mismatched) && mismatched.length > 0) {
    for (const row of mismatched.slice(0, 5)) {
      const r = row as { name?: unknown; expected?: unknown; actual?: unknown };
      if (typeof r.name !== "string") continue;
      const expected = typeof r.expected === "number" ? r.expected.toLocaleString() : "?";
      const actual = typeof r.actual === "number" ? r.actual.toLocaleString() : "?";
      notes.push(`${r.name}：${expected} のはずが ${actual} になっています`);
    }
    const rest = mismatched.length - 5;
    if (rest > 0) notes.push(`ほか ${String(rest)}件の科目でも食い違っています`);
  }

  /*
   * ★**除外した科目を黙って消さない。** 「配賦なし」の科目は C-01 の判定対象外なので、
   *   出さないと**設定を「配賦なし」にすれば検算が黙る**抜け道になる。
   */
  const notAllocated = d["notAllocatedCount"];
  const notAllocatedTotal = d["notAllocatedTotal"];
  if (typeof notAllocated === "number" && notAllocated > 0) {
    const amount =
      typeof notAllocatedTotal === "number" ? ` ${notAllocatedTotal.toLocaleString()}円` : "";
    notes.push(
      `日ごとに割り付けない設定の科目が ${String(notAllocated)}件${amount}あります。` +
        `この検算には入っていません`,
    );
  }
  // C-06 / C-13（小口現金）。★D-13 は「クレジットを現金出金に混ぜて30万円ずれた」件。混ぜていないことを passed でも言う
  const yenOf = (v: unknown): number => (typeof v === "number" ? v : 0);
  if (check.status !== "skipped") {
    if (yenOf(d["creditTotal"]) > 0) {
      notes.push(`クレジット払い ${yenOf(d["creditTotal"]).toLocaleString()}円は、現金の残高から引いていません`);
    }
    if (yenOf(d["unlinkedEntries"]) > 0) {
      notes.push(`科目が引けない小口現金の明細が ${String(yenOf(d["unlinkedEntries"]))}件あります（金額は数えています）`);
    }
    if (yenOf(d["refundsIn"]) > 0) {
      notes.push(`仕入の科目への入金 ${yenOf(d["refundsIn"]).toLocaleString()}円は、小口仕入に数えていません`);
    }
  }
  // C-12（税率）。★日付を出さないと、どの日を保存し直す／どの適用期間を見るかが決まらない
  const datesNote = (key: string, lead: string): void => {
    const dates = d[key];
    if (!Array.isArray(dates) || dates.length === 0) return;
    const rest = dates.length - 5;
    notes.push(`${lead}：${dates.slice(0, 5).join("、")}${rest > 0 ? ` ほか${String(rest)}日` : ""}`);
  };
  datesNote("mismatchedDates", "売上を保存したときの消費税率と、いまの設定が食い違う日");
  datesNote("unresolvedDates", "いま消費税率が設定されていない日");
  datesNote("unrecordedDates", "税率が記録されていない売上の日");
  return notes;
}

/**
 * 1件ぶんの説明文。★**空欄を作らない。**
 *
 * `skipped` は理由がそのまま説明。`failed` は差額を出す。
 * `passed` にも一言置く——**何も書かないと「見ていない」と区別が付かない。**
 */
export function describeCheck(check: IntegrityCheck): string {
  if (check.status === "skipped") {
    return check.skippedReason ?? "理由が記録されていません";
  }
  if (check.status === "failed") {
    /*
     * ★数字が出せるなら数字で言う。「一致しません」だけだと、
     *   どれくらいずれているのか（1円なのか30万円なのか）が分からず、
     *   直す順番を決められない。現行Excelの帳簿と実残高のずれは約30万円だった（D-13）。
     */
    if (check.expected !== null && check.actual !== null) {
      return (
        `期待 ${check.expected.toLocaleString()} に対して ${check.actual.toLocaleString()}` +
        (check.difference === null ? "" : `（差 ${check.difference.toLocaleString()}）`)
      );
    }
    if (check.difference !== null && check.difference !== 0) {
      return `差額 ${check.difference.toLocaleString()}円`;
    }
    return "期待どおりではありません";
  }
  return "確認しました";
}

/**
 * owner だけの操作（M10・`docs/05` §3.5 / §4）。★出すかどうかの判断だけ。担保はサーバの 403 と DB のトリガ。
 * 確定は、画面に出ている検算が全部通っているときだけ押せる（サーバは確定の直前にもう一度走らせる）。
 */
export interface OwnerActions {
  confirm: boolean;
  reject: boolean;
  reopen: boolean;
}

export function ownerActions(
  role: string,
  closingStatus: ClosingStatus,
  counts: ClosingSummary["counts"],
): OwnerActions {
  const owner = role === "owner";
  const submitted = owner && closingStatus === "SUBMITTED";
  return {
    confirm: submitted && counts.total > 0 && counts.failed === 0 && counts.skipped === 0,
    reject: submitted,
    reopen: owner && closingStatus === "CLOSED",
  };
}

/** 差戻し・解除の理由を、いまの状態に関係するほうだけ言う（古い理由を出し続けない） */
export function closingReasonNote(
  closingStatus: ClosingStatus,
  reasons: { rejectedReason: string | null; reopenedReason: string | null },
): string | null {
  if (closingStatus === "REJECTED" && reasons.rejectedReason !== null) {
    return `差し戻された月です。理由：${reasons.rejectedReason}。入力を直してから、もう一度締め申請してください。`;
  }
  if (closingStatus === "REOPENED" && reasons.reopenedReason !== null) {
    return `締めを解除した月です。理由：${reasons.reopenedReason}`;
  }
  return null;
}

/** owner が SUBMITTED の月を見ているとき、「オーナーの確定を待ってください」とは言わない（本人なので） */
export function ownerSubmittedReason(actions: OwnerActions): string | null {
  if (!actions.reject) return null;
  return actions.confirm
    ? "店長から締め申請が届いています。確定するか、差し戻してください。"
    : "店長から締め申請が届いていますが、自動検算が通っていないため、まだ確定できません。直すか、差し戻してください。";
}
